import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as faceapi from 'face-api.js';
import axios from 'axios';

const styles = {
  container: {
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)',
    color: '#fff',
    padding: '1rem',
    display: 'flex',
    flexDirection: 'column',
  },
  header: {
    textAlign: 'center',
    marginBottom: '1rem',
    paddingBottom: '1rem',
    borderBottom: '1px solid rgba(255,255,255,0.1)',
  },
  title: {
    fontSize: '1.5rem',
    color: '#00d9ff',
    marginBottom: '0.25rem',
  },
  subtitle: {
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
  },
  main: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
  },
  userInfo: {
    background: 'rgba(255,255,255,0.05)',
    borderRadius: '12px',
    padding: '1rem',
    width: '100%',
    maxWidth: '640px',
    display: 'flex',
    alignItems: 'center',
    gap: '1rem',
    border: '1px solid rgba(255,255,255,0.1)',
  },
  userAvatar: {
    width: '50px',
    height: '50px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
  },
  userName: {
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  userMeta: {
    fontSize: '0.85rem',
    color: 'rgba(255,255,255,0.6)',
  },
  videoContainer: {
    position: 'relative',
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: '0 10px 40px rgba(0, 0, 0, 0.3)',
    background: '#000',
    width: '100%',
    maxWidth: '640px',
  },
  video: {
    display: 'block',
    width: '100%',
    height: 'auto',
    transform: 'scaleX(-1)',
  },
  canvas: {
    position: 'absolute',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    transform: 'scaleX(-1)',
    pointerEvents: 'none',
  },
  progress: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: '0.5rem',
  },
  progressDot: {
    width: '12px',
    height: '12px',
    borderRadius: '50%',
    background: 'rgba(255,255,255,0.2)',
    transition: 'all 0.3s ease',
  },
  progressDotFilled: {
    background: '#00e676',
  },
  progressDotActive: {
    background: '#00d9ff',
    boxShadow: '0 0 10px #00d9ff',
  },
  status: {
    padding: '0.75rem 1.5rem',
    borderRadius: '8px',
    background: 'rgba(255, 255, 255, 0.1)',
    fontSize: '0.9rem',
    textAlign: 'center',
    maxWidth: '400px',
    width: '100%',
  },
  statusSuccess: {
    background: 'rgba(0, 230, 118, 0.2)',
    color: '#00e676',
    border: '1px solid rgba(0, 230, 118, 0.3)',
  },
  statusError: {
    background: 'rgba(255, 82, 82, 0.2)',
    color: '#ff5252',
    border: '1px solid rgba(255, 82, 82, 0.3)',
  },
  labelSelect: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginBottom: '0.5rem',
  },
  labelOption: {
    padding: '0.5rem 1rem',
    borderRadius: '20px',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    background: 'transparent',
    color: '#fff',
    fontSize: '0.85rem',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  labelOptionSelected: {
    background: 'rgba(0, 217, 255, 0.3)',
    borderColor: '#00d9ff',
    color: '#00d9ff',
  },
  labelOptionDone: {
    background: 'rgba(0, 230, 118, 0.3)',
    borderColor: '#00e676',
    color: '#00e676',
  },
  button: {
    padding: '1rem 2rem',
    borderRadius: '8px',
    border: 'none',
    fontSize: '1rem',
    fontWeight: '600',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
  },
  primaryButton: {
    background: 'linear-gradient(135deg, #00d9ff 0%, #0099cc 100%)',
    color: '#000',
  },
  successButton: {
    background: 'linear-gradient(135deg, #00e676 0%, #00c853 100%)',
    color: '#000',
  },
  secondaryButton: {
    background: 'rgba(255, 255, 255, 0.1)',
    color: '#fff',
    border: '1px solid rgba(255, 255, 255, 0.2)',
  },
  disabledButton: {
    opacity: 0.5,
    cursor: 'not-allowed',
  },
  buttonRow: {
    display: 'flex',
    gap: '1rem',
    marginTop: '1rem',
  },
  poweredBy: {
    textAlign: 'center',
    fontSize: '0.75rem',
    color: 'rgba(255,255,255,0.4)',
    marginTop: 'auto',
    paddingTop: '1rem',
  },
  loader: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: '1rem',
    padding: '3rem',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '4px solid rgba(255, 255, 255, 0.1)',
    borderTop: '4px solid #00d9ff',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  capturedList: {
    display: 'flex',
    gap: '0.5rem',
    flexWrap: 'wrap',
    justifyContent: 'center',
    marginTop: '0.5rem',
  },
  capturedChip: {
    padding: '0.25rem 0.75rem',
    background: 'rgba(0, 230, 118, 0.2)',
    borderRadius: '20px',
    fontSize: '0.8rem',
    color: '#00e676',
  },
};

const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const FACE_LABELS = [
  { id: 'frontal', label: 'Frontal', icon: '😐' },
  { id: 'smile', label: 'Smile', icon: '😊' },
  { id: 'left', label: 'Left', icon: '😏' },
];

function WidgetRegisterPage() {
  const [isModelLoaded, setIsModelLoaded] = useState(false);
  const [status, setStatus] = useState('Loading face detection models...');
  const [statusType, setStatusType] = useState('info');
  const [isProcessing, setIsProcessing] = useState(false);
  const [stream, setStream] = useState(null);
  const [config, setConfig] = useState({});
  const [personInfo, setPersonInfo] = useState(null);
  const [capturedFaces, setCapturedFaces] = useState([]);
  const [selectedLabel, setSelectedLabel] = useState('frontal');
  const [isComplete, setIsComplete] = useState(false);
  
  const videoRef = useRef();
  const canvasRef = useRef();

  // Parse URL parameters
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const cfg = {
      callback: params.get('callback') === 'true',
      source: params.get('source') || 'widget',
      parentOrigin: params.get('origin') || '*',
      external_user_id: params.get('external_user_id'),
      name: params.get('name') || 'User',
      department: params.get('department'),
      minFaces: parseInt(params.get('minFaces')) || 1,
    };
    setConfig(cfg);
    
    if (cfg.external_user_id && cfg.name) {
      setPersonInfo({
        external_user_id: cfg.external_user_id,
        name: cfg.name,
        department: cfg.department,
      });
    }
  }, []);

  // Load models
  useEffect(() => {
    const loadModels = async () => {
      try {
        const styleSheet = document.createElement('style');
        styleSheet.textContent = spinnerKeyframes;
        document.head.appendChild(styleSheet);

        await Promise.all([
          faceapi.nets.ssdMobilenetv1.loadFromUri('/models'),
          faceapi.nets.faceLandmark68Net.loadFromUri('/models'),
          faceapi.nets.faceRecognitionNet.loadFromUri('/models'),
        ]);
        
        setIsModelLoaded(true);
        
        // Check if person is registered, if not create them
        if (config.external_user_id) {
          await ensurePersonExists();
        }
        
        startCamera();
      } catch (error) {
        setStatus('Error loading models: ' + error.message);
        setStatusType('error');
      }
    };
    
    if (config.external_user_id) {
      loadModels();
    }
    
    return () => {
      if (stream) {
        stream.getTracks().forEach(track => track.stop());
      }
    };
  }, [config.external_user_id]);

  const ensurePersonExists = async () => {
    try {
      // Try to create person (will return existing if already exists)
      const person_id = `ext_${config.source}_${config.external_user_id}`;
      
      try {
        await axios.post('/api/persons', {
          person_id,
          name: config.name,
          department: config.department,
          external_user_id: config.external_user_id,
        });
      } catch (e) {
        // Person might already exist, that's ok
      }
      
      // Get current face count
      const response = await axios.get(`/api/faces/${person_id}`);
      if (response.data.faces) {
        setCapturedFaces(response.data.faces.map(f => f.label));
      }
      
      setPersonInfo({
        person_id,
        external_user_id: config.external_user_id,
        name: config.name,
        department: config.department,
      });
    } catch (error) {
      console.error('Error ensuring person exists:', error);
    }
  };

  const startCamera = async () => {
    try {
      const newStream = await navigator.mediaDevices.getUserMedia({
        video: { width: 640, height: 480, facingMode: 'user' }
      });
      
      setStream(newStream);
      
      setTimeout(() => {
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
          videoRef.current.play().catch(console.error);
        }
        setStatus('Position your face and capture different angles.');
        setStatusType('info');
      }, 100);
    } catch (error) {
      setStatus('Camera error: ' + error.message);
      setStatusType('error');
    }
  };

  const sendResultToParent = useCallback((data) => {
    if (config.callback && window.opener) {
      window.opener.postMessage({
        type: data.success ? 'FACE_REGISTERED' : 'FACE_REGISTRATION_FAILED',
        source: config.source,
        ...data,
      }, config.parentOrigin);
    }
  }, [config]);

  const captureFace = useCallback(async () => {
    if (!isModelLoaded || !videoRef.current || isProcessing || !personInfo) return;
    
    setIsProcessing(true);
    setStatus('Detecting face...');
    setStatusType('info');
    
    try {
      const detection = await faceapi
        .detectSingleFace(videoRef.current)
        .withFaceLandmarks()
        .withFaceDescriptor();

      if (detection) {
        // Draw detection
        if (canvasRef.current) {
          const displaySize = { 
            width: videoRef.current.videoWidth, 
            height: videoRef.current.videoHeight 
          };
          faceapi.matchDimensions(canvasRef.current, displaySize);
          const resized = faceapi.resizeResults(detection, displaySize);
          const ctx = canvasRef.current.getContext('2d');
          ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          faceapi.draw.drawDetections(canvasRef.current, resized);
          faceapi.draw.drawFaceLandmarks(canvasRef.current, resized);
        }

        const faceVector = Array.from(detection.descriptor);
        const labelInfo = FACE_LABELS.find(l => l.id === selectedLabel);
        const label = `${labelInfo.icon} ${labelInfo.label}`;
        
        const response = await axios.post('/api/faces/register', {
          person_id: personInfo.person_id,
          face_vector: faceVector,
          label,
          source_type: 'camera',
          confidence: detection.detection.score,
        });
        
        if (response.data.success) {
          const newCaptured = [...capturedFaces, label];
          setCapturedFaces(newCaptured);
          setStatus(`✅ Captured: ${label} (${newCaptured.length}/5)`);
          setStatusType('success');
          
          // Auto-select next label
          const currentIndex = FACE_LABELS.findIndex(l => l.id === selectedLabel);
          if (currentIndex < FACE_LABELS.length - 1) {
            setSelectedLabel(FACE_LABELS[currentIndex + 1].id);
          }
          
          // Clear canvas after delay
          setTimeout(() => {
            if (canvasRef.current) {
              const ctx = canvasRef.current.getContext('2d');
              ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            }
          }, 1000);
        }
      } else {
        setStatus('No face detected. Please try again.');
        setStatusType('error');
      }
    } catch (error) {
      setStatus('Error: ' + (error.response?.data?.error || error.message));
      setStatusType('error');
    }
    
    setIsProcessing(false);
  }, [isModelLoaded, isProcessing, personInfo, selectedLabel, capturedFaces]);

  const handleComplete = () => {
    setIsComplete(true);
    
    sendResultToParent({
      success: true,
      external_user_id: config.external_user_id,
      person_id: personInfo.person_id,
      faces_registered: capturedFaces.length,
    });
    
    setStatus(`✅ Registration complete! ${capturedFaces.length} faces registered.`);
    setStatusType('success');
    
    if (stream) {
      stream.getTracks().forEach(track => track.stop());
    }
  };

  const handleCancel = () => {
    if (config.callback && window.opener) {
      window.opener.postMessage({
        type: 'FACE_REGISTRATION_CANCELLED',
        source: config.source,
      }, config.parentOrigin);
    }
    window.close();
  };

  const handleClose = () => {
    window.close();
  };

  if (!config.external_user_id) {
    return (
      <div style={styles.container}>
        <div style={styles.loader}>
          <p style={{ color: '#ff5252' }}>
            Error: external_user_id is required.
          </p>
          <p style={{ fontSize: '0.85rem', color: 'rgba(255,255,255,0.6)' }}>
            Please provide user info in URL parameters.
          </p>
        </div>
      </div>
    );
  }

  if (!isModelLoaded) {
    return (
      <div style={styles.container}>
        <div style={styles.loader}>
          <div style={styles.spinner} />
          <p>{status}</p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>📷 Face Registration</h1>
        {config.source && (
          <p style={styles.subtitle}>Source: {config.source}</p>
        )}
      </div>
      
      <div style={styles.main}>
        {/* User Info */}
        {personInfo && (
          <div style={styles.userInfo}>
            <div style={styles.userAvatar}>👤</div>
            <div>
              <div style={styles.userName}>{personInfo.name}</div>
              <div style={styles.userMeta}>
                {personInfo.department && `${personInfo.department} • `}
                ID: {personInfo.external_user_id}
              </div>
            </div>
          </div>
        )}

        {!isComplete ? (
          <>
            {/* Progress */}
            <div style={styles.progress}>
              <span style={{ marginRight: '0.5rem', fontSize: '0.9rem' }}>Faces:</span>
              {[...Array(5)].map((_, i) => (
                <div
                  key={i}
                  style={{
                    ...styles.progressDot,
                    ...(i < capturedFaces.length ? styles.progressDotFilled : {}),
                    ...(i === capturedFaces.length ? styles.progressDotActive : {}),
                  }}
                />
              ))}
              <span style={{ marginLeft: '0.5rem', color: '#00d9ff' }}>
                {capturedFaces.length}/5
              </span>
            </div>

            {/* Video */}
            <div style={styles.videoContainer}>
              <video
                ref={videoRef}
                autoPlay
                muted
                playsInline
                onLoadedMetadata={(e) => e.target.play()}
                style={styles.video}
              />
              <canvas ref={canvasRef} style={styles.canvas} />
            </div>

            {/* Label Selection */}
            <div style={styles.labelSelect}>
              {FACE_LABELS.map(label => {
                const isDone = capturedFaces.some(f => f.includes(label.label));
                return (
                  <button
                    key={label.id}
                    style={{
                      ...styles.labelOption,
                      ...(selectedLabel === label.id ? styles.labelOptionSelected : {}),
                      ...(isDone ? styles.labelOptionDone : {}),
                    }}
                    onClick={() => setSelectedLabel(label.id)}
                  >
                    {label.icon} {label.label} {isDone && '✓'}
                  </button>
                );
              })}
            </div>
            
            {/* Status */}
            <div
              style={{
                ...styles.status,
                ...(statusType === 'error' ? styles.statusError : {}),
                ...(statusType === 'success' ? styles.statusSuccess : {}),
              }}
            >
              {status}
            </div>

            {/* Captured List */}
            {capturedFaces.length > 0 && (
              <div style={styles.capturedList}>
                {capturedFaces.map((face, i) => (
                  <span key={i} style={styles.capturedChip}>{face}</span>
                ))}
              </div>
            )}
            
            {/* Buttons */}
            <div style={styles.buttonRow}>
              <button
                style={{
                  ...styles.button,
                  ...styles.primaryButton,
                  ...(isProcessing || capturedFaces.length >= 5 ? styles.disabledButton : {}),
                }}
                onClick={captureFace}
                disabled={isProcessing || capturedFaces.length >= 5}
              >
                {isProcessing ? '⏳ Capturing...' : '📸 Capture Face'}
              </button>
              
              {capturedFaces.length >= config.minFaces && (
                <button
                  style={{ ...styles.button, ...styles.successButton }}
                  onClick={handleComplete}
                >
                  ✅ Complete ({capturedFaces.length} faces)
                </button>
              )}
              
              {config.callback && (
                <button
                  style={{ ...styles.button, ...styles.secondaryButton }}
                  onClick={handleCancel}
                >
                  Cancel
                </button>
              )}
            </div>
          </>
        ) : (
          <>
            {/* Completion Card */}
            <div style={{
              background: 'rgba(0, 230, 118, 0.1)',
              border: '2px solid rgba(0, 230, 118, 0.5)',
              borderRadius: '12px',
              padding: '2rem',
              textAlign: 'center',
              maxWidth: '400px',
            }}>
              <div style={{ fontSize: '4rem', marginBottom: '1rem' }}>✅</div>
              <h2 style={{ color: '#00e676', marginBottom: '0.5rem' }}>Registration Complete!</h2>
              <p style={{ color: 'rgba(255,255,255,0.7)' }}>
                {capturedFaces.length} face(s) registered successfully.
              </p>
            </div>
            
            <button
              style={{ ...styles.button, ...styles.primaryButton }}
              onClick={handleClose}
            >
              Close Window
            </button>
          </>
        )}
      </div>
      
      <div style={styles.poweredBy}>
        Powered by FaceGate Recognition System
      </div>
    </div>
  );
}

export default WidgetRegisterPage;

